import { useEffect, useState } from 'react';
import { FC } from 'react';
import { Card, Box, CircularProgress } from '@mui/material';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State as ODataState } from '@progress/kendo-data-query';
import myCustomersApi from '../../api/MyCustomersApi';
import useAuth from '../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { ShowAttendee } from 'src/types/showAttendee';
import ShowConfirmationNumberCell from '../grid/ShowConfirmationNumberCell';
import FilterOperators from '../grid/FilterOperators';
import ColumnMenu from '../grid/ColumnMenu';
import useSettings from 'src/hooks/useSettings';
import { SETTING_KEYS } from 'src/constants';
import { getPageSizeSetting } from 'src/utils/settingsHelper';

const CustomerShowAttendees: FC<React.PropsWithChildren<unknown>> = () => {
  const CustomerShowAttendeesPageSizeKey = SETTING_KEYS.CustomerShowAttendeesPageSize;
  const { user } = useAuth();
  const [showAttendees, setShowAttendees] = useState<ShowAttendee[]>([]);
  const [total, setTotal] = useState(0);
  const [search] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const savePagingSettings = useSettings((state) => state.savePagingSettings);
  const userSettings = useSettings((state) => state.userSettings);

  const pageSize = getPageSizeSetting(userSettings, CustomerShowAttendeesPageSizeKey, 50);
  const [defaultFilter] = useState<CompositeFilterDescriptor | FilterDescriptor>({
    filters: [
      { field: 'salesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'lawnAndGardenSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'mcSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'plantSalesRepId', value: user.salesReps[0], operator: 'eq' },
    ],
    logic: 'or',
  });
  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: [defaultFilter],
      logic: 'and',
    },
    skip: 0,
    take: pageSize,
    sort: [{ field: 'customerName', dir: 'asc' }],
  });

  const loadShowAttendees = async () => {
    setIsLoading(true);
    myCustomersApi
      .getCustomerShowAttendees(gridDataState)
      .then((response) => {
        setShowAttendees(response.value);
        setTotal(response['@odata.count']);
        setIsLoading(false);
      })
      .catch(() => {
        setShowAttendees([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];
    if (search.get('allcustomers') !== 'true') filters.push(defaultFilter);

    const newDataState: ODataState = {
      ...gridDataState,
      filter: {
        filters: filters,
        logic: 'and',
      },
      skip: 0,
      take: pageSize,
      sort: [{ field: 'customerName', dir: 'asc' }],
    };

    setGridDataState(newDataState);
  }, [search]);

  useEffect(() => {
    loadShowAttendees();
  }, [gridDataState]);

  const handlePageChange = (e) => {
    const newPageSize = e.page.take;
    setGridDataState((prevState) => ({
      ...prevState,
      skip: e.page.skip,
      take: newPageSize,
    }));
    savePagingSettings(newPageSize, CustomerShowAttendeesPageSizeKey);
  };

  return (
    <div className="progress-table-container">
      {isLoading && <CircularProgress></CircularProgress>}
      <Card>
        <Box
          sx={{
            minWidth: 500,
          }}
        ></Box>
        <Grid
          data={showAttendees}
          {...gridDataState}
          pageable={{
            buttonCount: 4,
            pageSizes: [20, 50, 100, 500],
            pageSizeValue: pageSize,
          }}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          total={total}
          sortable
          filterOperators={FilterOperators}
          columnMenu={ColumnMenu}
          resizable={true}
          scrollable="scrollable"
          onDataStateChange={(e) => setGridDataState(e.dataState)}
        >
          <GridColumn field="showName" title="Show Name" width={260} />
          <GridColumn field="customerName" title="Customer Name" width={260} />
          <GridColumn field="customerNumber" title="Customer Number" width={140} />
          <GridColumn field="lastName" title="Last Name" width={120} />
          <GridColumn field="firstName" title="First Name" width={150} />
          <GridColumn field="emailAddress" title="Email" width={200} />
          <GridColumn field="guestCount" title="Guests" width={80} />
          <GridColumn field="confirmationNumber" title="Confirmation" cells={{ data: ShowConfirmationNumberCell }} width={120} />
          <GridColumn field="salesRepId" title="Grower Rep" width={100} />
          <GridColumn field="lawnAndGardenSalesRepId" title="L&G Rep" width={80} />
        </Grid>
      </Card>
    </div>
  );
};
export default CustomerShowAttendees;
